import HBList from "@/components/HBList";
import Image from "@/components/Image";
import { useLogin } from "@/components/LoginModal";
import useAppStore from "@/context/store";
import { getTopicPins } from "@/services";
import { getContentType } from "@/utils/getContentType";
import imageUrl from "@/utils/imageUrl";
import useRequest from "@/utils/useReq";
import { useSetState } from "ahooks";
import classNames from "classnames";
import { useCallback, useEffect, useState } from "react";
import styles from "../index.module.less";

const masonryOptions = {
  threshold: 200,
  gutter: 2,
  columnSize: 82.5,
  brickId: "pin_id",
};

const scrollableTarget = "pinTopicPinList";

const infiniteScrollOptions = {
  endMessage: null,
  loader: null,
};

interface PinTopicPinListProps {
  related_topic: ITopic;
  onSelect: (pin: IPin, currentSrc: string, source: string) => void;
  pinId: number;
}

export default function PinTopicPinList({
  related_topic,
  onSelect,
  pinId,
}: PinTopicPinListProps) {
  const user = useAppStore((s) => s.user);
  const { login } = useLogin();
  const [query, setQuery] = useSetState({
    per_page: 20,
    page: 2,
  });
  const [hasMore, setHasMore] = useState(true);
  const [dataSource, setDataSource] = useState(related_topic?.pins || []);
  useRequest(
    async () => {
      const result = await getTopicPins({
        filter_ids: [related_topic.filter_id],
        ...query,
      });
      const pins = result?.pins || [];

      if (pins.length <= 0) {
        setHasMore(false);
      }

      setDataSource(dataSource.concat(pins));
    },
    {
      refreshDeps: [query],
    }
  );

  useEffect(() => {
    if (related_topic) {
      setHasMore(true);
      setDataSource(related_topic?.pins || []);
      setQuery({
        page: 2,
      });
    }
  }, [related_topic, setQuery]);

  const onLoadMore = useCallback(() => {
    const pins = related_topic?.pins || [];
    if (dataSource.length > pins.length) {
      setQuery((prev) => ({
        page: prev.page + 1,
      }));
    }
  }, [dataSource, related_topic, setQuery]);

  const render = useCallback(
    (pin) => {
      const height = Math.min(
        260,
        Math.round((pin.file?.height / pin.file?.width) * 82.5)
      );

      const onClick = async (evt) => {
        if (evt.metaKey || evt.ctrlKey) {
          return;
        }

        evt.preventDefault();
        if (!user) {
          login();
        }

        onSelect(pin, imageUrl(pin.file, { width: 82.5 }), "sameTopic");
      };

      const onImgLoad = (evt) => {
        const target = evt.target;
        target?.classList.add("transparent-img-bg");
      };

      return (
        <a
          className={classNames([
            styles.item,
            pinId === pin?.pin_id ? styles.active : null,
          ])}
          style={{ height }}
          key={pin.pin_id}
          href={`/pins/${pin.pin_id}`}
          onClick={onClick}
          data-gd-expose="content_expose"
          data-content-type={getContentType(pin?.extra)}
          data-gd-click="material_click"
          data-content-id={pin.pin_id}
          data-material-id={pin.extra?.material_id}
          data-board-id={pin.board_id}
          data-file-id={pin.file_id}
          data-ext={JSON.stringify(pin?.ext)}
        >
          <div className={styles.img}>
            <Image
              onLoad={onImgLoad}
              src={imageUrl(pin.file, { width: 82.5 })}
              width={82.5}
              height={Math.round((pin.file?.height / pin.file?.width) * 82.5)}
              alt={pin.raw_text}
              showError={true}
            />
          </div>
        </a>
      );
    },
    [pinId, user, onSelect, login]
  );

  return (
    <div
      className={styles.preview}
      id={scrollableTarget}
      data-content-source="同画板"
    >
      <HBList
        dataSource={dataSource}
        uniqueKey={"pin_id"}
        scrollableTarget={scrollableTarget}
        render={render}
        hasMore={hasMore}
        onLoadMore={onLoadMore}
        masonryOptions={masonryOptions}
        infiniteScrollOptions={infiniteScrollOptions}
      />
    </div>
  );
}
